import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Match the directory page (`src/layouts/coaches-search`) so navigating
    // between the directory and the results screen feels like one continuous
    // surface. `theme.palette.background.default` is `#F7F7F7`.
    backgroundColor: theme.palette.background.default,
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    paddingTop: theme.spacing(2),
    paddingBottom: 0,
    height: '100%',
  },
  headerShell: {
    flexShrink: 0,
    marginBottom: theme.spacing(1.5),
  },
  searchHeaderPanel: {
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #e8ecf2',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
    overflow: 'hidden',
  },
  contextSection: {
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.palette.common.white,
  },
  sectionDivider: {
    backgroundColor: '#e0e6ed',
  },
  filterSection: {
    padding: theme.spacing(1.25, 2),
  },
  contextFields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5, 2),
    alignItems: 'center',
  },
  contextField: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 1 auto',
    minWidth: 0,
    maxWidth: '100%',
  },
  contextFieldCaption: {
    display: 'inline-block',
    color: '#7c8797',
    fontWeight: 500,
    fontSize: theme.typography.pxToRem(18),
    lineHeight: 1.2,
    marginBottom: 0,
    whiteSpace: 'nowrap',
  },
  contextDropdownTrigger: {
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textTransform: 'none',
    textAlign: 'left',
    border: '1px solid #e6ebf1',
    borderRadius: 8,
    backgroundColor: '#f2f5f8',
    padding: theme.spacing(0.875, 1.5),
    height: 42,
    minHeight: 42,
    minWidth: 172,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#eef2f6',
      borderColor: '#d7dee8',
    },
  },
  contextDropdownTriggerOpen: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomColor: 'transparent',
  },
  contextDropdownValue: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.text.primary,
    paddingRight: theme.spacing(1),
  },
  contextDropdownCaret: {
    flexShrink: 0,
    color: '#6f7988',
    width: 18,
    height: 18,
    transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shorter }),
  },
  contextDropdownCaretOpen: {
    transform: 'rotate(180deg)',
  },
  /** Persona (or similar) differs from default — matches active filter chips on legacy coach search. */
  contextControlActive: {
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9',
    '&:hover': {
      borderColor: '#3268A7',
      backgroundColor: '#ECF2F9',
    },
    '& $contextDropdownValue': {
      color: '#3268A7',
    },
    '& $contextDropdownCaret': {
      color: '#3268A7',
    },
  },
  contextMenuPaperAttached: {
    marginTop: '-1px !important',
    borderTopLeftRadius: '0 !important',
    borderTopRightRadius: '0 !important',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    border: '1px solid #dfe3ea',
    borderTop: 'none',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.14)',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  contextNearAutocomplete: {
    minWidth: 230,
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f2f5f8',
      borderRadius: 8,
      minHeight: 42,
      paddingRight: theme.spacing(1),
      '& fieldset': {
        borderColor: '#e6ebf1',
      },
      '&:hover fieldset': {
        borderColor: '#d7dee8',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: theme.spacing(0.875),
      paddingBottom: theme.spacing(0.875),
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(18),
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: theme.spacing(0.5),
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: '#6f7988',
    },
  },
  contextAutocompletePaper: {
    marginTop: theme.spacing(0.5),
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.14)',
    border: '1px solid #dfe3ea',
  },
  contextLocationIcon: {
    color: '#3268A7',
  },
  filterToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    /** Keep Lucide and legacy MUI glyphs aligned inside 36px pills. */
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  formatChips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  /** Radius / price / availability — same gap as format chips so the row stays even. */
  iconFilterGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  /** Format toggles: outlined pill, no shadow (toolbar chip style). */
  formatChip: {
    height: 'auto',
    minHeight: 36,
    borderRadius: 999,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    backgroundColor: `${theme.palette.common.white} !important`,
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    paddingTop: theme.spacing(0.375),
    paddingBottom: theme.spacing(0.375),
    paddingLeft: theme.spacing(0.25),
    paddingRight: theme.spacing(0.25),
    '&:hover, &:focus': {
      backgroundColor: `${theme.palette.common.white} !important`,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiChip-icon': {
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(0.75),
      marginRight: theme.spacing(-0.625),
      width: 16,
      height: 16,
    },
  },
  formatChipSelected: {
    color: '#3268A7 !important',
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9 !important',
    boxShadow: 'none',
    '&:hover, &:focus': {
      borderColor: '#3268A7',
      backgroundColor: '#ECF2F9 !important',
      boxShadow: 'none',
    },
    '& .MuiChip-icon': {
      color: '#3268A7',
    },
  },
  /** Private format: one pill matching filter toolbar buttons (split toggle + menu trigger). */
  privateChipRoot: {
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: 999,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    overflow: 'hidden',
    height: 36,
    minHeight: 36,
  },
  privateChipRootSelected: {
    borderColor: '#3268A7',
    backgroundColor: '#ECF2F9',
    '& $privateChipToggleButton': {
      color: '#3268A7',
      '& .MuiButton-startIcon': {
        color: '#3268A7',
      },
    },
    '& $privateChipMenuTrigger': {
      color: '#3268A7',
      borderLeftColor: '#3268A7',
    },
    '& $privateChipToggleButton:hover, & $privateChipMenuTrigger:hover': {
      backgroundColor: 'rgba(50, 104, 167, 0.08)',
    },
  },
  privateChipToggleButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    borderRadius: 0,
    border: 'none',
    padding: theme.spacing(0.375, 0.875),
    height: 36,
    minHeight: 36,
    minWidth: 0,
    flexShrink: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(15, 34, 52, 0.06)',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.375),
      marginLeft: theme.spacing(0.25),
      color: theme.palette.text.secondary,
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  privateChipMenuTrigger: {
    borderRadius: 0,
    border: 'none',
    borderLeft: '1px solid #dfe3ea',
    padding: theme.spacing(0.375, 0.75),
    height: 36,
    minWidth: 36,
    minHeight: 36,
    flexShrink: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(15, 34, 52, 0.06)',
      boxShadow: 'none',
    },
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  privateCategoryCheckbox: {
    padding: 4,
    marginRight: 4,
    color: '#143d66',
    '&.Mui-checked': {
      color: '#143d66',
    },
  },
  privateCategoryAllSpacer: {
    width: 28,
  },
  toolbarDivider: {
    width: 1,
    height: 36,
    alignSelf: 'center',
    backgroundColor: '#dfe3ea',
    margin: 0,
    flexShrink: 0,
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  filterPill: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    borderRadius: 999,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(0.375, 1.25),
    height: 36,
    minHeight: 36,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      color: theme.palette.text.secondary,
      '& svg': {
        width: 16,
        height: 16,
      },
    },
    '& .MuiButton-endIcon': {
      color: theme.palette.text.secondary,
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  /** Icon + chevron only; 36px height matches format chips; even inner icon spacing. */
  filterPillIconOnly: {
    padding: theme.spacing(0.375, 0.75),
    height: 36,
    minWidth: 36,
    minHeight: 36,
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
    },
    '& .MuiButton-startIcon': {
      margin: 0,
    },
    '& .MuiButton-endIcon': {
      margin: 0,
    },
  },
  filterPillLabel: {
    marginLeft: theme.spacing(0.25),
    marginRight: theme.spacing(0.25),
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filterMenuPaper: {
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(15, 50, 84, 0.08)',
    boxShadow: '0 6px 20px rgba(15, 34, 52, 0.12), 0 2px 6px rgba(15, 34, 52, 0.06)',
    minWidth: 260,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    '& ul': {
      padding: 0,
    },
  },
  /** Extra offset below the Private chip + chevron so the category menu clears the control row. */
  privateCategoryMenuPaper: {
    marginTop: theme.spacing(1.5),
  },
  filterMenuItem: {
    borderRadius: 0,
    marginLeft: 0,
    marginRight: 0,
    paddingTop: theme.spacing(1.125),
    paddingBottom: theme.spacing(1.125),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: 44,
    '&:hover:not(.Mui-selected)': {
      backgroundColor: '#f0f2f5',
    },
  },
  filterMenuItemSelected: {
    backgroundColor: '#143d66 !important',
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: '#0f3254 !important',
    },
    '& .MuiTypography-root': {
      color: theme.palette.common.white,
      fontWeight: 600,
    },
    '& $filterMenuLeading': {
      color: theme.palette.common.white,
    },
  },
  filterMenuLeading: {
    minWidth: 32,
    marginRight: theme.spacing(0.5),
    color: '#143d66',
  },
  filterMenuLeadingSpacer: {
    display: 'inline-block',
    width: 18,
    height: 18,
  },
  filterMenuBetweenSections: {
    listStyle: 'none',
    backgroundColor: 'rgba(15, 34, 52, 0.12)',
    height: 1,
    margin: theme.spacing(0.5, 0),
    padding: 0,
    minHeight: 0,
    border: 'none',
  },
  toggleButtonActive: {
    color: '#3268A7 !important',
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#3268A7',
      backgroundColor: '#ECF2F9',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      color: '#3268A7',
    },
    '& .MuiButton-endIcon': {
      color: '#3268A7',
    },
  },
  /** Save + map/list view — keep on one row next to each other. */
  saveToolbarGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(1.25),
    flexShrink: 0,
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    borderRadius: 999,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.375, 1.25),
    height: 36,
    minHeight: 36,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      color: theme.palette.text.secondary,
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  savedSearchCountButton: {
    minWidth: 0,
    borderRadius: 999,
    fontWeight: 600,
    textTransform: 'none',
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0.375, 0.875),
    height: 36,
    minHeight: 36,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.5),
      color: theme.palette.text.secondary,
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.25),
      color: '#6b7280',
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  savedSearchMenuPaper: {
    borderRadius: 12,
    border: '1px solid rgba(15, 50, 84, 0.08)',
    boxShadow: '0 10px 30px rgba(15, 34, 52, 0.16), 0 2px 8px rgba(15, 34, 52, 0.08)',
    minWidth: 320,
    marginTop: theme.spacing(1),
    overflow: 'hidden',
  },
  savedSearchMenuHeader: {
    padding: theme.spacing(2, 2, 0.5),
  },
  savedSearchMenuEyebrow: {
    margin: 0,
    color: '#7c8797',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  savedSearchMenuItem: {
    padding: theme.spacing(0.75, 2, 2),
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  savedSearchMenuItemContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  savedSearchMenuLabel: {
    color: '#293241',
    fontSize: theme.typography.pxToRem(18),
    lineHeight: 1.45,
  },
  savedSearchRemoveButton: {
    color: '#6b7280',
    padding: theme.spacing(0.5),
    marginTop: -2,
  },
  savedSearchMenuSeparator: {
    display: 'inline-block',
    margin: theme.spacing(0, 1.75),
    color: '#7c8797',
  },
  /** Circular saved-offerings toggle: diameter matches icon-only pills (44px incl. border), centered icon inset. */
  savedOfferingsFilterCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: 36,
    height: 36,
    minWidth: 36,
    maxWidth: 36,
    minHeight: 36,
    maxHeight: 36,
    padding: 0,
    borderRadius: '50%',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.primary,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      display: 'inline-flex',
      color: theme.palette.text.secondary,
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  savedOfferingsFilterCircleActive: {
    color: '#3268A7 !important',
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#3268A7',
      backgroundColor: '#ECF2F9',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      display: 'inline-flex',
      color: '#3268A7',
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  /** Persistent view toggle: desktop uses split/full list, mobile swaps map vs list. */
  layoutViewToggleButton: {
    minWidth: 82,
    borderRadius: 999,
    fontWeight: 600,
    textTransform: 'none',
    border: '1px solid #3268A7',
    boxShadow: 'none',
    backgroundColor: '#ECF2F9',
    color: '#3268A7',
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0.375, 1.25),
    height: 36,
    minHeight: 36,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#ECF2F9',
      borderColor: '#3268A7',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      color: '#3268A7',
      '& svg': {
        width: 16,
        height: 16,
      },
    },
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  meta: {
    color: theme.palette.text.secondary,
  },
  contentArea: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      gap: 0,
      paddingBottom: 0,
    },
  },
  resultsColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 100%',
      width: '100%',
    },
  },
  resultsPanel: {
    padding: theme.spacing(3),
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #e8ecf2',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
    height: '100%',
    overflowY: 'auto',
    boxSizing: 'border-box',
    touchAction: 'pan-y',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      boxShadow: 'none',
      border: 'none',
      padding: theme.spacing(1),
    },
  },
  mapColumn: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    height: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 100%',
      width: '100%',
    },
  },
  mapPanel: {
    height: '100%',
    overflow: 'hidden',
    borderRadius: 16,
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },
  resultsHeader: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  // Loading state next to the results headline. Sits inline with an h5
  // navy headline, so we use the brand blue accent (`blue.main` = #3268A7)
  // to clearly read as "live status" without competing with the headline
  // the way the previous gold (`primary.main`) did.
  updatingText: {
    color: theme.palette.blue.main,
    fontWeight: 700,
  },
  // Mirrors the directory `PersonaBanner` (`PopularGoals.styles.js#banner`)
  // so any "soft inline notice" surface across the coach search experience
  // reads as the same affordance — light grey-blue fill, hairline border,
  // 12px radius, primary-text color, body-2 typography. The sparkle icon
  // there is persona-specific so we don't carry it over here.
  notice: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderRadius: 12,
    backgroundColor: '#F4F6FA',
    border: '1px solid #D5D7DC',
    color: theme.palette.text.primary,
    fontSize: 14,
    [theme.breakpoints.down('xs')]: {
      fontSize: 13,
    },
  },
  resultsList: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(2),
  },
  loadMoreZone: {
    height: theme.spacing(32),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 3),
    border: '1px solid #dfe3ea',
    borderRadius: 20,
    backgroundColor: '#fbfcfe',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 1),
    },
  },
  emptyIconShell: {
    width: 88,
    height: 88,
    margin: `0 auto ${theme.spacing(3)}px`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(230, 188, 90, 0.18)',
  },
  emptyIcon: {
    fontSize: theme.typography.pxToRem(34),
    color: theme.palette.gold.main,
  },
  emptyActions: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  emptyActionButton: {
    minHeight: 48,
    padding: theme.spacing(1, 3),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.secondary.light,
    border: `1px solid ${theme.palette.secondary.light}`,
    backgroundColor: '#FAFAFA',
    boxShadow: 'none',
    '&:hover': {
      border: `1px solid ${theme.palette.secondary.light}`,
      backgroundColor: theme.palette.blue.light100,
      color: theme.palette.blue.main,
      boxShadow: 'none',
    },
  },
  emptyTextActionButton: {
    minHeight: 'auto',
    height: 'auto',
    padding: theme.spacing(0.5, 1),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    color: theme.palette.secondary.light,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.blue.main,
      boxShadow: 'none',
    },
  },
  emptyDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(4, 0, 0),
  },
  emptyDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#d9e1ec',
  },
  emptyDividerBadge: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eef3fb',
    color: '#65748b',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    letterSpacing: '0.08em',
  },
  emptyContentBlock: {
    maxWidth: 560,
    margin: '0 auto',
  },
  emptyContentHeading: {
    marginBottom: theme.spacing(1),
    color: '#65748b',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  emptyContentTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(34),
    lineHeight: 1.15,
    color: theme.palette.text.primary,
  },
  emptyContentDescription: {
    maxWidth: 460,
    margin: '0 auto',
    fontSize: theme.typography.pxToRem(18),
    lineHeight: 1.6,
  },
  emptyPrimaryButton: {
    minHeight: 48,
    padding: theme.spacing(1, 4),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  fallbackSection: {
    width: '100%',
    textAlign: 'left',
    paddingTop: theme.spacing(4),
  },
  fallbackSectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  fallbackRail: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  fallbackCard: {
    textAlign: 'left',
  },
  saveMessage: {
    marginTop: theme.spacing(1),
  },
  /** Mobile-only row: filter visibility toggle + Map/List view toggle. Hidden on desktop. */
  mobileControlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  mobileFilterToggleButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0.375, 1.25),
    height: 36,
    minHeight: 36,
    borderRadius: 999,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
  },
  mobileFilterToggleButtonActive: {
    color: '#3268A7',
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9',
    '&:hover': {
      backgroundColor: '#ECF2F9',
      borderColor: '#3268A7',
      boxShadow: 'none',
    },
  },
  '@media (max-width:959.95px)': {
    contextSection: {
      padding: theme.spacing(1.25, 1.5),
    },
    contextField: {
      flexWrap: 'wrap',
      width: '100%',
      gap: theme.spacing(0.75, 1),
    },
    contextDropdownTrigger: {
      width: '100%',
      minWidth: 0,
    },
    contextFieldCaption: {
      fontSize: theme.typography.pxToRem(16),
    },
    contextDropdownValue: {
      fontSize: theme.typography.pxToRem(16),
    },
    contextNearAutocomplete: {
      minWidth: 0,
      width: '100%',
      '& .MuiOutlinedInput-input': {
        fontSize: theme.typography.pxToRem(16),
      },
    },
    emptyDivider: {
      gap: theme.spacing(1.25),
    },
    emptyDividerBadge: {
      width: 48,
      height: 48,
    },
    emptyIconShell: {
      width: 72,
      height: 72,
      marginBottom: theme.spacing(2.5),
    },
    emptyIcon: {
      fontSize: theme.typography.pxToRem(28),
    },
    emptyContentTitle: {
      fontSize: theme.typography.pxToRem(28),
    },
    emptyContentDescription: {
      fontSize: theme.typography.pxToRem(16),
    },
  },
}))

export default useStyles
